import React, { FC, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ModalName } from 'app/enums/modal-name';
import { Alert, Box, Typography } from '@mui/material';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { useStyles } from '../PaymentModal.styles';
import { AutoSwapForm } from './AutoSwapForm';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { useAutoSwapForm } from 'modules/payments/hooks/use-auto-swap-form';
import { getAutoSwapSettings } from 'modules/payments/api/get-auto-swap-setting';
import { useCancelToken } from 'app/services/api/hooks';
import { useDispatch } from 'react-redux';
import { modalsReducers } from 'app/store/modal/slice';
import { saveAutoSwapSetting } from 'modules/payments/api/save-auto-swap-setting';
import { toastSuccess } from 'modules/utils';
import InfoIcon from '@mui/icons-material/Info';
import { userPaymentAccountsSelector } from 'modules/payments/store/user-payment-accounts/selectors';
import { exchangeRatesSelector } from 'modules/payments/store/exchange-rates/selectors';
import { SWAP_MIN_AMOUNTS, SystemCurrency } from '@okletsplay/core';

const AutoSwapModalComponent: FC = () => {
  const { classes } = useStyles();
  const cancelToken = useCancelToken();
  const dispatch = useDispatch();

  const userAccounts = useSelector(userPaymentAccountsSelector.all);
  const exchangeRates = useSelector(exchangeRatesSelector.all);

  const { form } = useAutoSwapForm(
    () => userAccounts,
    () => exchangeRates
  );
  const {
    reset,
    getValues,
    formState: { isValid, isValidating },
  } = form;

  const [isLoading, setLoading] = useState(true);

  const close = useCallback(() => {
    dispatch(modalsReducers.closeModal(ModalName.BalanceAutoSwap));
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    getAutoSwapSettings({ cancelToken }).then((data) => {
      reset(data);
      setLoading(false);
    }, close);
  }, [cancelToken, close, reset]);

  const handleSave = useCallback(() => {
    if (!isValid || isValidating) return;

    setLoading(true);
    saveAutoSwapSetting({ ...getValues(), cancelToken })
      .then(() => toastSuccess('Auto-swap settings have been saved'))
      .finally(close);
  }, [cancelToken, close, getValues, isValid, isValidating]);

  return (
    <Modal
      name={ModalName.BalanceAutoSwap}
      title="Auto-Swap Settings"
      contentHeight={390}
      maxWidth="md"
      actions={
        <LoaderButton
          loading={isLoading}
          disabled={!isValid}
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          onClick={handleSave}
        >
          Save settings
        </LoaderButton>
      }
    >
      <Box className={classes.root}>
        <Box mb={2}>
          <Alert severity="info" icon={<InfoIcon className={classes.infoIcon} />}>
            <Typography mb={1}>
              You can setup auto swap OKLP for USDT. When your OKLP balance goes lower than
              threshold, we will try to buy the amount of OKLP.
            </Typography>
            <Typography>
              Minimum purchase amount is {SWAP_MIN_AMOUNTS[SystemCurrency.USDT]} USDT, as OKLP/USDT
              exchange rate fluctuates.
            </Typography>
            <Typography>
              Real time exchange rate will be used, swap platform fee will be applied on top.
            </Typography>
          </Alert>
        </Box>
        <Box mb={2}>
          <AutoSwapForm form={form} isDisabled={isLoading} exchangeRates={exchangeRates} />
        </Box>
      </Box>
    </Modal>
  );
};

export const AutoSwapModal = withModalOpenChecker(
  AutoSwapModalComponent,
  ModalName.BalanceAutoSwap
);
